import React, {useCallback, useContext, useEffect, useRef} from 'react';
import Subshops from './Subshops.jsx';
import {
    applyFilterLandmark,
    deleteFilterParams,
    getContentAjaxUrl,
    mergeSelectedFiltersWithParams,
    yieldToMain,
} from '../ArticleOverviewHelper';
import NitroEventHelper from '@core/scripts/helper/nitro-event-helper';
import Landmark from '@core/scripts/components/landmark-adapter';

import {Constants} from '../view-constants';
import {setContentAjaxCall} from '../AjaxCallSlice.jsx';
import {useDispatch, useSelector} from 'react-redux';
import {articleOverviewStaticContext} from '../ArticleOverviewStatic.jsx';
import {disableSticky, enableSticky, selectFilterStatus} from '../FilterSlice.jsx';
import {useBreakpointUp} from 'bonprix-design-system';
import FilterOrderingButton from '@article-overview/components/header/FilterOrderingButton.jsx';
import Filter from '@article-overview/components/filter/Filter.jsx';
import Device from '@core/scripts/components/device.js';

function FilterContainer({
    filterContent,
    landmarkContent,
    ordering,
    showOnlyTokens,
    filledArticleList,
    rootShopName,
    subshopsTopOffset,
}) {
    const {
        contentAjaxUrl,
        prettyUrl,
        currentSearchParams,
        rootCategoryPath,
        filterRootShopId,
        shopId,
        currentUrlPath,
        currentCategoryPath,
    } = useContext(articleOverviewStaticContext);

    const stickyFilterLine = useRef(null);
    const breakpointLUp = useBreakpointUp('l');
    const breakpointXLUp = useBreakpointUp('xl');

    const isApp = Device.isApp();

    const isSticky = useSelector(selectFilterStatus).sticky;

    const dispatch = useDispatch();

    useEffect(() => {
        if (!stickyFilterLine.current) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    dispatch(disableSticky());
                } else {
                    dispatch(enableSticky());
                }
            },
            {
                root: null,
                threshold: 0,
                rootMargin: `-${breakpointXLUp ? subshopsTopOffset : 0}px 0px 0px 0px`,
            },
        );

        observer.observe(stickyFilterLine.current);

        return () => observer.disconnect();
    }, [subshopsTopOffset, breakpointXLUp]);

    const filterSubmit = useCallback(
        async (clickedFilters, clickedSortOption, retainShopIdFilterOnly = false) => {
            await yieldToMain(() => {
                let mergedParams = mergeSelectedFiltersWithParams(clickedFilters, currentSearchParams.current);

                applyFilterLandmark(
                    clickedFilters,
                    mergedParams,
                    shopId.current,
                    isSticky,
                    currentSearchParams.current,
                    filterRootShopId.current,
                );
                NitroEventHelper.sendTracedEvent('FILTER_PRODUCT_LIST');

                if (clickedSortOption) {
                    mergedParams.set(Constants.SEARCH_PARAMS_ORDERING, clickedSortOption.value);
                    Landmark.sendLandmark({
                        landmark: 'userinterface',
                        click: clickedSortOption.lm,
                        fixation: isSticky ? Constants.LANDMARK_STICKY : Constants.LANDMARK_NON_STICKY,
                    });
                    NitroEventHelper.sendTracedEvent('SORT_PRODUCT_LIST');
                }

                const path =
                    clickedFilters.get(Constants.CATEGORY_FILTER_IDENTIFIER)?.path || currentCategoryPath.current;

                mergedParams.delete(Constants.SEARCH_PARAMS_PAGE);
                mergedParams.delete(Constants.SEARCH_PARAMS_ANCHOR);

                if (retainShopIdFilterOnly) {
                    mergedParams = deleteFilterParams(mergedParams);
                }

                const formattedParams = `?${mergedParams.toString()}`;

                currentUrlPath.current = currentUrlPath.current.replace(currentCategoryPath.current, path);
                currentCategoryPath.current = path;
                currentSearchParams.current = mergedParams;

                const contentAjaxCall = getContentAjaxUrl(path, formattedParams, contentAjaxUrl);
                dispatch(setContentAjaxCall([contentAjaxCall, retainShopIdFilterOnly]));
            });
        },
        [
            prettyUrl.current,
            rootCategoryPath.current,
            shopId.current,
            filterContent.shopIdFilterGroup,
            currentSearchParams.current,
            currentCategoryPath.current,
            currentUrlPath.current,
            isSticky,
        ],
    );

    return (
        <>
            <div className="sticky-filter-line" ref={stickyFilterLine}></div>

            {filterContent.shopIdFilterGroup && filledArticleList && (
                <Subshops
                    identifier={filterContent.shopIdFilterGroup.identifier}
                    trackingId={filterContent.shopIdFilterGroup.filters[0]?.trackingId}
                    subshops={filterContent.shopIdFilterGroup.filters[0]?.filterOptions}
                    links={filterContent.shopIdFilterGroup.links}
                    filterSubmit={filterSubmit}
                    rootShopName={rootShopName}
                    subshopsTopOffset={subshopsTopOffset}
                />
            )}

            {!showOnlyTokens && !breakpointLUp && (
                <div className={`article-overview__sticky-mobile-filter-wrapper${isApp ? ' -is-app' : ''}`}>
                    <FilterOrderingButton filterViewValue={landmarkContent?.filterViewLandmark} />
                </div>
            )}

            <Filter filterContent={filterContent} filterSubmit={filterSubmit} ordering={ordering} />
        </>
    );
}

export default React.memo(FilterContainer);
